import { gql } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import apollo, { API_CMS_PROVIDER } from "@/helpers/apollo";
import { CITY_FIELDS, cities2regions } from "@/helpers/cities";
import HomePage from "@/pages/home";
import type { DataType } from "@/pages/home/types";

const fetchHomeData = createServerFn({ method: "GET" }).handler(async () => {
  const client = apollo();

  const query = gql`
    ${CITY_FIELDS}

    {
      cities(
        filters: { parentCity: { id: null } }
        sort: "nameShort:asc"
        pagination: { page: 1, pageSize: 1000 }
      ) {
        ...CityFields
      }
      homepage {
        data {
          attributes {
            blogLinks {
              coverImage {
                data {
                  attributes {
                    url
                  }
                }
              }
              id
              title
              url
            }
            ctaFactoids {
              description
              id
              title
            }
            ctaTitle
            downloadCtaSubtitle
            downloadCtaTitle
            faq(pagination: { pageSize: 1000 }) {
              answer
              id
              question
            }
            heroTitle
            heroSubtitle
          }
        }
      }
    }
  `;

  const response = await client.query<DataType>({
    context: { clientName: API_CMS_PROVIDER },
    query,
  });

  const { data } = response;

  return {
    ...data,
    attractionCount: data.homepage.data.attributes.ctaFactoids[0].title,
    regions: cities2regions(data.cities.data),
  };
});

export const Route = createFileRoute("/")({
  component: Home,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { attractionCount } = loaderData;
    const faq = loaderData.homepage.data.attributes.faq;
    const description = `Embark on ${attractionCount} immersive adventures with Morty. Dive into endless excitement and create unforgettable memories. Start your journey now.`;

    return {
      links: [{ href: process.env.APP_URL || "", rel: "canonical" }],
      meta: [
        { title: "Morty: Every escape room. Every haunt. Ever." },
        { content: description, name: "description" },
        { content: description, property: "og:description" },
        {
          content: `${process.env.APP_URL}/images/share.jpg`,
          property: "og:image",
        },
        {
          content: "Morty: Every escape room. Every haunt. Ever.",
          property: "og:title",
        },
        { content: "website", property: "og:type" },
        { content: process.env.APP_URL || "", property: "og:url" },
        { content: "summary_large_image", name: "twitter:card" },
        { content: "@mortyapp", name: "twitter:site" },
        { content: "Morty: Escape Rooms & Haunts", property: "og:site_name" },
        {
          "script:ld+json": {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Morty: Escape Rooms & Haunts",
            url: process.env.APP_URL,
          },
        },
        {
          "script:ld+json": {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map(
              (item: { answer: string; question: string }) => ({
                "@type": "Question",
                acceptedAnswer: { "@type": "Answer", text: item.answer },
                name: item.question,
              })
            ),
          },
        },
      ],
    };
  },
  headers: () => ({
    "Cache-Control": "s-maxage=3600, stale-while-revalidate=86400",
  }),
  loader: () => fetchHomeData(),
});

function Home() {
  const data = Route.useLoaderData();
  return <HomePage {...data} />;
}
